import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { professionalClientDashboardService } from '../../../services/professionalClientDashboardService';
import { supabase } from '../../../lib/supabase';

const MesExpeditions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    period: 'all',
    type: 'all',
    search: ''
  });
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [professionalClient, setProfessionalClient] = useState(null);

  useEffect(() => {
    // Get professional client info first
    const fetchClientInfo = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user) {
          const { data: client } = await supabase
            ?.from('professional_clients')
            ?.select('email')
            ?.eq('auth_user_id', user?.id)
            ?.single();
          setProfessionalClient(client);
        }
      } catch (err) {
        console.error('Error fetching client info:', err);
      }
    };

    fetchClientInfo();
  }, []);

  useEffect(() => {
    loadShipments();

    // Setup real-time subscriptions when client email is available
    if (!professionalClient?.email) return;

    console.log('Setting up real-time subscriptions for shipments:', professionalClient?.email);

    // Subscribe to shipment and event updates
    const subscription = professionalClientDashboardService?.subscribeToShipmentUpdates(
      professionalClient?.email,
      (update) => {
        console.log('Real-time shipment update received:', update);
        // Reload shipments list when data changes
        loadShipments();
      }
    );

    // Cleanup subscription on unmount
    return () => {
      console.log('Cleaning up shipment subscriptions');
      subscription?.unsubscribe();
    };
  }, [professionalClient?.email]);

  const loadShipments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await professionalClientDashboardService?.getShipments();
      setShipments(data);
    } catch (err) {
      console.error('Error loading shipments:', err);
      setError(err?.message || 'Erreur lors du chargement des expéditions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (shipmentId) => {
    try {
      const details = await professionalClientDashboardService?.getShipmentDetails(shipmentId);
      setSelectedShipment(details);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error loading shipment details:', err);
      alert('Erreur lors du chargement des détails');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      delivered: { label: 'Livré', color: 'bg-green-100 text-green-800', icon: 'CheckCircle' },
      in_transit: { label: 'En transit', color: 'bg-yellow-100 text-yellow-800', icon: 'Truck' },
      pending: { label: 'En préparation', color: 'bg-blue-100 text-blue-800', icon: 'Clock' },
      exception: { label: 'Problème', color: 'bg-red-100 text-red-800', icon: 'AlertCircle' },
      customs: { label: 'Douane', color: 'bg-purple-100 text-purple-800', icon: 'FileText' },
      out_for_delivery: { label: 'En livraison', color: 'bg-orange-100 text-orange-800', icon: 'Package' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={16} />
        {config?.label}
      </span>
    );
  };

  const filteredShipments = shipments?.filter(shipment => {
    // Status filter
    if (filters?.status !== 'all') {
      if (filters?.status === 'en_cours' && !['in_transit', 'customs', 'out_for_delivery']?.includes(shipment?.current_status)) return false;
      if (filters?.status === 'livres' && shipment?.current_status !== 'delivered') return false;
      if (filters?.status === 'en_attente' && shipment?.current_status !== 'pending') return false;
    }

    // Period filter
    if (filters?.period !== 'all') {
      const shipmentDate = new Date(shipment?.created_at);
      const now = new Date();
      const monthsAgo = {
        'ce_mois': 0,
        '3_mois': 3,
        '6_mois': 6,
        'cette_annee': 12
      }?.[filters?.period];

      if (monthsAgo !== undefined) {
        const cutoffDate = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1);
        if (shipmentDate < cutoffDate) return false;
      }
    }

    // Type filter
    if (filters?.type !== 'all' && shipment?.shipment_type !== filters?.type) return false;

    // Search filter
    if (filters?.search && !shipment?.reference?.toLowerCase()?.includes(filters?.search?.toLowerCase())) return false;

    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des expéditions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="AlertCircle" size={24} className="text-red-600" />
          <h3 className="text-lg font-semibold text-red-900">Erreur</h3>
        </div>
        <p className="text-red-700 mb-4">{error}</p>
        <Button onClick={loadShipments} variant="outline">
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Mes expéditions</h1>
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
            <select
              value={filters?.status}
              onChange={(e) => setFilters({ ...filters, status: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="all">Tous</option>
              <option value="en_cours">En cours</option>
              <option value="livres">Livrés</option>
              <option value="en_attente">En attente</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Période</label>
            <select
              value={filters?.period}
              onChange={(e) => setFilters({ ...filters, period: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="all">Tout</option>
              <option value="ce_mois">Ce mois</option>
              <option value="3_mois">3 derniers mois</option>
              <option value="6_mois">6 mois</option>
              <option value="cette_annee">Cette année</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={filters?.type}
              onChange={(e) => setFilters({ ...filters, type: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="all">Tous</option>
              <option value="Volume">Volume</option>
              <option value="Barriques">Barriques</option>
              <option value="Conteneur complet">Conteneur complet</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
            <input
              type="text"
              placeholder="N° de référence"
              value={filters?.search}
              onChange={(e) => setFilters({ ...filters, search: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
      </div>
      {/* Expeditions Table */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Référence</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Date de départ</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Volume</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Montant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredShipments?.map((shipment) => (
                <tr key={shipment?.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{shipment?.reference}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{format(new Date(shipment?.created_at), 'd MMM yyyy', { locale: fr })}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{shipment?.shipment_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{shipment?.volume}</td>
                  <td className="px-4 py-3">{getStatusBadge(shipment?.current_status)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{shipment?.amount} €</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewDetails(shipment?.id)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Voir détails
                      </button>
                      <button className="text-green-600 hover:text-green-800">
                        <Icon name="Download" size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Detail Modal */}
      {selectedShipment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Détails de l'expédition</h2>
              <button
                onClick={() => setSelectedShipment(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <Icon name="X" size={24} className="text-gray-700" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Info Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Référence</p>
                  <p className="font-bold text-gray-900">{selectedShipment?.reference}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Date de départ</p>
                  <p className="font-bold text-gray-900">{format(new Date(selectedShipment?.created_at), 'd MMMM yyyy', { locale: fr })}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Type d'envoi</p>
                  <p className="font-bold text-gray-900">{selectedShipment?.shipment_type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Volume</p>
                  <p className="font-bold text-gray-900">{selectedShipment?.volume}</p>
                </div>
              </div>

              {/* Photos Gallery */}
              {selectedShipment?.photos?.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Photos d'expédition</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {selectedShipment?.photos?.map((photo, index) => (
                      <img
                        key={index}
                        src={photo}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-40 object-cover rounded-lg border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
                      />
                    ))}
                  </div>
                  <Button className="mt-3" size="sm" variant="outline">
                    <Icon name="Download" size={16} className="mr-2" />
                    Télécharger toutes les photos
                  </Button>
                </div>
              )}

              {/* Documents */}
              {selectedShipment?.documents?.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Documents liés</h3>
                  <div className="space-y-2">
                    {selectedShipment?.documents?.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <div className="flex items-center space-x-3">
                          <Icon name="FileText" size={20} className="text-blue-600" />
                          <div>
                            <p className="font-medium text-gray-900">{doc?.nom}</p>
                            <p className="text-xs text-gray-500">{doc?.type}</p>
                          </div>
                        </div>
                        <button className="text-green-600 hover:text-green-800">
                          <Icon name="Download" size={20} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Timeline */}
              {selectedShipment?.timeline?.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Timeline détaillée</h3>
                  <div className="space-y-4">
                    {selectedShipment?.timeline?.map((event, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full mt-1"></div>
                        <div>
                          <p className="font-medium text-gray-900">{event?.event}</p>
                          <p className="text-sm text-gray-500">{event?.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MesExpeditions;